<!--
  ProgressiveImageUploader.vue
  
  Ce composant est une version améliorée de DeliveryFileUpload qui
  commence à uploader les images dès qu'elles sont prises (si en ligne)
  tout en permettant le stockage local lorsque hors ligne.
-->
<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from '@/api/axios'
import localforage from 'localforage'

// Props
const props = defineProps({
  deliveryId: {
    type: [Number, String],
    required: true
  },
  deliveryContext: {
    type: String,
    default: 'shop_delivery',
    validator: (value) => ['emplacement','fruits', 'anomalie', 'consignes'].includes(value)
  },
  preferredCamera: {
    type: String,
    default: 'environment', // 'environment' (arrière) ou 'user' (avant)
    validator: (value) => ['environment', 'user'].includes(value)
  },
  imageQuality: {
    type: Number, 
    default: 0.85, // Réduit la qualité par défaut de 0.95 à 0.85
    validator: (value) => value >= 0 && value <= 1
  },
  maxWidth: {
    type: Number,
    default: 1280 // Réduit de 2400 à 1280
  },
  maxHeight: {
    type: Number,
    default: 1280 // Réduit de 2400 à 1280
  },
  thumbnailWidth: {
    type: Number,
    default: 300 // Ajoute une dimension pour les miniatures
  },
  thumbnailHeight: {
    type: Number,
    default: 300
  }
})

// Emits
const emit = defineEmits(['success', 'error', 'upload-progress', 'upload-complete', 'upload-error'])

// Store pour accéder au statut réseau
const store = useStore()
const isOnline = computed(() => store.getters['sync/isOnline'])

// Instance localforage pour les images temporaires (pendant l'upload)
const pendingImagesStore = localforage.createInstance({
  name: 'images',
  storeName: 'pending_uploads'
})

// État local
const image = ref(null)
const loading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const error = ref(null)
const fileInput = ref(null)
const uploadedServerId = ref(null)
const localImageUrl = ref(null)
const abortController = ref(null)
const isAndroid = ref(false)
const isIOS = ref(false)
const deviceInfo = ref(null)
const browserInfo = ref(null)
const cancelTimer = ref(null)
const showCancelButton = ref(false)

// Détecter le type d'appareil et le navigateur
onMounted(() => {
  detectDeviceAndBrowser()
})

// Fonction pour détecter l'appareil et le navigateur
const detectDeviceAndBrowser = () => {
  const ua = navigator.userAgent.toLowerCase()
  isAndroid.value = /android/.test(ua)
  isIOS.value = /iphone|ipad|ipod/.test(ua)
  
  // Détection du navigateur
  const isSafari = /safari/.test(ua) && !/chrome/.test(ua)
  const isChrome = /chrome|chromium/.test(ua)
  const isFirefox = /firefox/.test(ua)
  
  browserInfo.value = {
    isSafari,
    isChrome,
    isFirefox,
    name: isSafari ? 'safari' : isChrome ? 'chrome' : isFirefox ? 'firefox' : 'other'
  }
  
  deviceInfo.value = {
    platform: isAndroid.value ? 'android' : 
              isIOS.value ? 'ios' : 'web',
    model: navigator.platform || 'unknown',
    userAgent: navigator.userAgent,
    browserName: browserInfo.value.name
  }
  
  console.log('Appareil détecté:', deviceInfo.value)
}

// Nettoyage à la destruction du composant
onUnmounted(() => {
  if (localImageUrl.value) {
    URL.revokeObjectURL(localImageUrl.value)
  }
  
  // Libérer également les URLs des miniatures
  if (image.value?.thumbnailUrl) {
    URL.revokeObjectURL(image.value.thumbnailUrl)
  }
  
  if (image.value?.path && image.value.path.startsWith('blob:')) {
    URL.revokeObjectURL(image.value.path)
  }
  
  if (abortController.value) {
    abortController.value.abort()
  }
  if (cancelTimer.value) {
    clearTimeout(cancelTimer.value)
  }
})

// Obtenir l'URL d'une image
const getImageUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('blob:') || path.startsWith('data:')) return path
  if (path.startsWith('http')) return path
  return `${import.meta.env.VITE_APP_BASE_URL}${path}`
}

// Méthode pour prendre une photo
const takePicture = async () => {
  // Empêcher les actions pendant le chargement
  if (loading.value || uploading.value) return
  
  try {
    loading.value = true
    error.value = null
    showCancelButton.value = false
    
    // Montrer un bouton d'annulation après quelques secondes
    cancelTimer.value = setTimeout(() => {
      showCancelButton.value = true
    }, 5000) // Afficher le bouton après 5 secondes
    
    // Simplification: on n'essaie plus de vérifier la permission avec getUserMedia
    // On utilise directement l'input file avec capture
    
    // Cliquer sur l'input pour ouvrir l'appareil photo
    if (fileInput.value) {
      fileInput.value.click()
    }
  } catch (err) {
    console.error('Erreur lors de la prise de photo:', err)
    
    if (err.message === 'User cancelled photo capture') {
      console.log('Prise de photo annulée par l\'utilisateur')
    } else {
      error.value = 'Échec de la prise de photo. Veuillez réessayer.'
    }
    
    if (error.value) {
      emit('error', error.value)
    }
  } finally {
    // S'assurer que loading est toujours remis à false
    loading.value = false
    showCancelButton.value = false
    if (cancelTimer.value) {
      clearTimeout(cancelTimer.value)
      cancelTimer.value = null
    }
  }
}

// Ajouter un timestamp sur l'image
const formatTimestamp = () => {
  const now = new Date()
  const date = now.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const time = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  return `${date} ${time}`
}

// Corriger l'orientation de l'image
const getImageOrientation = (file) => {
  return new Promise((resolve) => {
    try {
      // Essayer de lire les métadonnées EXIF pour obtenir l'orientation
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const view = new DataView(event.target.result)
          const length = view.byteLength
          
          // Vérifier si l'image est un JPEG et si le buffer est assez grand
          if (length < 2 || view.getUint16(0, false) !== 0xFFD8) {
            resolve(1) // Pas un JPEG ou buffer trop petit
            return
          }
          
          let offset = 2
          
          // Boucle sécurisée avec vérification de limites à chaque étape
          while (offset + 2 <= length) {
            // Vérifier qu'on peut lire le marker
            const marker = view.getUint16(offset, false)
            offset += 2
            
            // Vérifier si on a trouvé le marqueur EXIF
            if (marker === 0xFFE1) {
              // S'assurer qu'il y a assez d'espace pour lire le header EXIF
              if (offset + 6 > length) {
                resolve(1)
                return
              }
              
              // Vérifier la signature Exif
              if (view.getUint32(offset + 2, false) !== 0x45786966) {
                resolve(1) // Pas de données EXIF
                return
              }
              
              // S'assurer qu'il y a assez d'espace pour lire l'endianness
              if (offset + 10 > length) {
                resolve(1)
                return
              }
              
              const little = view.getUint16(offset + 8, false) === 0x4949
              offset += 8
              
              // S'assurer qu'il y a assez d'espace pour lire le nombre de tags
              if (offset + 2 > length) {
                resolve(1)
                return
              }
              
              const tags = view.getUint16(offset, little)
              offset += 2
              
              // Vérifier que le nombre de tags est raisonnable pour éviter les boucles infinies
              if (tags > 128) {
                resolve(1)
                return
              }
              
              // Parcourir les tags en vérifiant les limites
              for (let i = 0; i < tags; i++) {
                // Vérifier qu'on peut accéder au tag
                if (offset + (i * 12) + 2 > length) {
                  resolve(1)
                  return
                }
                
                // Vérifier que c'est le tag d'orientation
                if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                  // Vérifier qu'on peut accéder à la valeur du tag
                  if (offset + (i * 12) + 10 > length) {
                    resolve(1)
                    return
                  }
                  
                  const orientation = view.getUint16(offset + (i * 12) + 8, little)
                  resolve(orientation)
                  return
                }
              }
              
              // Si on n'a pas trouvé le tag d'orientation
              resolve(1)
              return
            } else if ((marker & 0xFF00) !== 0xFF00) {
              // Marqueur invalide, on arrête
              break
            } else {
              // Vérifier qu'on peut lire la taille du segment
              if (offset + 2 > length) {
                resolve(1)
                return
              }
              
              // Passer au segment suivant, mais vérifier qu'on ne dépasse pas les limites
              const segmentSize = view.getUint16(offset, false)
              if (segmentSize < 2 || offset + segmentSize > length) {
                // Taille de segment invalide ou dépassement de buffer
                resolve(1)
                return
              }
              
              offset += segmentSize
            }
          }
          
          // Si on arrive ici, on n'a pas trouvé d'informations d'orientation
          resolve(1)
        } catch (error) {
          console.error('Erreur lors de l\'analyse EXIF:', error)
          resolve(1) // En cas d'erreur, utiliser l'orientation par défaut
        }
      }
      
      reader.onerror = () => {
        console.error('Erreur lors de la lecture du fichier pour l\'orientation')
        resolve(1) // En cas d'erreur, utiliser l'orientation par défaut
      }
      
      // Lire seulement les premiers 64KB pour les métadonnées
      reader.readAsArrayBuffer(file.slice(0, 64 * 1024))
    } catch (error) {
      console.error('Erreur lors de l\'extraction de l\'orientation:', error)
      resolve(1) // En cas d'erreur, utiliser l'orientation par défaut
    }
  })
}

// Compresser l'image et ajouter un horodatage
const compressImage = async (file) => {
  try {
    const orientation = await getImageOrientation(file)
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)

      reader.onload = (event) => {
        const img = new Image()
        img.onload = () => {
          // Créer un canvas pour la manipulation d'image
          const canvas = document.createElement('canvas')
          // Déterminer les dimensions en tenant compte de l'orientation
          let width = img.width
          let height = img.height

          // Ajuster les dimensions si nécessaire
          const MAX_WIDTH = props.maxWidth
          const MAX_HEIGHT = props.maxHeight

          // Détection des appareils récents (haut de gamme) pour compression supplémentaire
          const isHighEndDevice = navigator.hardwareConcurrency > 4 && navigator.deviceMemory > 4
          
          // Déterminer le facteur de redimensionnement
          let scaleFactor = 1;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              scaleFactor = MAX_WIDTH / width;
            }
          } else {
            if (height > MAX_HEIGHT) {
              scaleFactor = MAX_HEIGHT / height;
            }
          }
          
          // Pour les appareils haut de gamme avec des photos très haute résolution, 
          // réduire davantage pour éviter la surcharge mémoire
          if (isHighEndDevice && (width > 3000 || height > 3000)) {
            scaleFactor *= 0.75; // Réduction supplémentaire
          }
          
          width = Math.floor(width * scaleFactor);
          height = Math.floor(height * scaleFactor);

          // Définir les dimensions du canvas
          const ctx = canvas.getContext('2d')
          
          // Appliquer les transformations en fonction de l'orientation EXIF
          switch (orientation) {
            case 2: // Horizontal flip
              canvas.width = width
              canvas.height = height
              ctx.translate(width, 0)
              ctx.scale(-1, 1)
              break
            case 3: // 180° rotation
              canvas.width = width
              canvas.height = height
              ctx.translate(width, height)
              ctx.rotate(Math.PI)
              break
            case 4: // Vertical flip
              canvas.width = width
              canvas.height = height
              ctx.translate(0, height)
              ctx.scale(1, -1)
              break
            case 5: // Horizontal flip + 90° rotation
              canvas.width = height
              canvas.height = width
              ctx.rotate(0.5 * Math.PI)
              ctx.scale(-1, 1)
              break
            case 6: // 90° rotation
              canvas.width = height
              canvas.height = width
              ctx.translate(height, 0)
              ctx.rotate(0.5 * Math.PI)
              break
            case 7: // Horizontal flip + 270° rotation
              canvas.width = height
              canvas.height = width
              ctx.rotate(0.5 * Math.PI)
              ctx.translate(-height, -width)
              ctx.scale(-1, 1)
              break
            case 8: // 270° rotation
              canvas.width = height
              canvas.height = width
              ctx.translate(0, width)
              ctx.rotate(-0.5 * Math.PI)
              break
            default: // cas 1 - orientation normale
              canvas.width = width
              canvas.height = height
          }

          // Dessiner l'image avec la bonne orientation
          if ([5, 6, 7, 8].includes(orientation)) {
            ctx.drawImage(img, 0, 0, img.height * scaleFactor, img.width * scaleFactor)
          } else {
            ctx.drawImage(img, 0, 0, width, height)
          }

          // Ajouter le timestamp
          const timestamp = formatTimestamp()
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
          ctx.font = '16px Arial'
          const textMetrics = ctx.measureText(timestamp)
          const textWidth = textMetrics.width
          const padding = 8
          const textHeight = 20

          ctx.fillRect(
            canvas.width - textWidth - padding * 2,
            canvas.height - textHeight - padding * 2,
            textWidth + padding * 2,
            textHeight + padding * 2
          )

          ctx.fillStyle = 'rgba(0, 0, 0, 0.9)'
          ctx.fillText(
            timestamp,
            canvas.width - textWidth - padding,
            canvas.height - padding - 6
          )

          // Ajouter des informations sur l'appareil si disponible
          if (deviceInfo.value) {
            const deviceText = deviceInfo.value.model || deviceInfo.value.platform
            if (deviceText) {
              const deviceTextMetrics = ctx.measureText(deviceText)
              const deviceTextWidth = deviceTextMetrics.width
              
              ctx.fillRect(
                canvas.width - deviceTextWidth - padding * 2,
                canvas.height - textHeight * 2 - padding * 4,
                deviceTextWidth + padding * 2,
                textHeight + padding * 2
              )
              
              ctx.fillText(
                deviceText,
                canvas.width - deviceTextWidth - padding,
                canvas.height - textHeight - padding * 4
              )
            }
          }

          // Générer le blob avec la qualité configurée
          const quality = props.imageQuality
          
          // Adapter la qualité en fonction de la taille de l'image originale
          let adaptiveQuality = quality;
          if (file.size > 5 * 1024 * 1024) { // Si > 5MB
            adaptiveQuality = Math.min(quality, 0.75); // Réduire davantage
          }
          
          canvas.toBlob((blob) => {
            if (!blob) {
              console.error('Échec de la génération du blob')
              reject(new Error('Échec de la génération de l\'image'))
              return
            }
            
            resolve(new File([blob], file.name || 'image.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now()
            }))
          }, 'image/jpeg', adaptiveQuality)
        }

        img.onerror = () => {
          console.error('Erreur lors du chargement de l\'image')
          reject(new Error('Échec du chargement de l\'image'))
        }
        
        img.src = event.target.result
      }

      reader.onerror = () => {
        console.error('Erreur lors de la lecture du fichier')
        reject(new Error('Échec de la lecture du fichier'))
      }
    })
  } catch (err) {
    console.error('Erreur lors du traitement de l\'orientation:', err)
    // En cas d'échec du traitement de l'orientation, on essaie de continuer sans
    return new Promise((resolve, reject) => {
      // [code similaire sans correction d'orientation]
      // On utilise le code original pour éviter de répéter tout le code
      const reader = new FileReader()
      reader.readAsDataURL(file)
      
      // [reste du code original]
      // ... existing compressImage code without orientation correction ...
    })
  }
}

// Créer une miniature pour l'affichage
const createThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    
    reader.onload = (event) => {
      const img = new Image()
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        // Déterminer les dimensions pour maintenir le ratio
        let width = props.thumbnailWidth
        let height = props.thumbnailHeight
        
        // Calculer le ratio pour conserver les proportions
        const ratio = Math.min(width / img.width, height / img.height)
        width = img.width * ratio
        height = img.height * ratio
        
        // Centrer l'image dans le canvas
        canvas.width = props.thumbnailWidth
        canvas.height = props.thumbnailHeight
        
        // Remplir avec du blanc ou du gris léger
        ctx.fillStyle = '#f1f1f1'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        // Dessiner l'image centrée
        const x = (canvas.width - width) / 2
        const y = (canvas.height - height) / 2
        
        ctx.drawImage(img, x, y, width, height)
        
        // Convertir en URL de données
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Échec de génération de la miniature'))
            return
          }
          resolve(URL.createObjectURL(blob))
        }, 'image/jpeg', 0.7) // Qualité réduite pour la miniature
      }
      
      img.onerror = () => {
        reject(new Error('Échec du chargement pour la miniature'))
      }
      
      img.src = event.target.result
    }
    
    reader.onerror = () => {
      reject(new Error('Échec de lecture pour la miniature'))
    }
  })
}

// Traiter un fichier image
const processFile = async (file) => {
  try {
    loading.value = true;
    
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      throw new Error('Type de fichier non supporté. Veuillez sélectionner une image.');
    }
    
    // Limiter la taille du fichier (20MB)
    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('L\'image est trop volumineuse. La taille maximale est de 20MB.');
    }
    
    // Essayer de compresser l'image
    let compressedFile;
    try {
      compressedFile = await compressImage(file);
    } catch (compressionError) {
      console.error('Erreur lors de la compression:', compressionError);
      // En cas d'échec de compression, utiliser le fichier original
      console.log('Utilisation du fichier original suite à l\'échec de compression');
      compressedFile = file;
    }
    
    // Vérifier la taille après compression
    if (compressedFile.size > MAX_FILE_SIZE) {
      throw new Error('L\'image reste trop volumineuse après compression. Veuillez essayer avec une image de résolution inférieure.');
    }
    
    // Créer une miniature pour l'affichage
    let thumbnailUrl;
    try {
      thumbnailUrl = await createThumbnail(compressedFile);
    } catch (thumbnailError) {
      console.error('Erreur lors de la création de la miniature:', thumbnailError);
      // Utiliser l'image compressée comme miniature en cas d'échec
      thumbnailUrl = URL.createObjectURL(compressedFile);
    }
    
    // Créer l'objet image avec toutes les données nécessaires
    const imageData = {
      id: `img_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
      file: compressedFile,
      fileName: file.name || `image_${Date.now()}.jpg`,
      fileType: file.type || 'image/jpeg',
      path: URL.createObjectURL(compressedFile),
      thumbnailUrl: thumbnailUrl, // Ajouter la miniature
      deliveryId: props.deliveryId,
      deliveryContext: props.deliveryContext,
      timestamp: new Date().toISOString(),
      source: isAndroid.value ? 'android' : isIOS.value ? 'ios' : 'web',
      browser: browserInfo.value?.name || 'unknown',
      synced: false,
      retryCount: 0, // Nombre de tentatives d'upload
      initialFileSize: file.size,
      compressedFileSize: compressedFile.size,
      // S'assurer que c'est identifié comme une livraison standard
      type: 'delivery',
      size: compressedFile.size
    };
    
    if (deviceInfo.value) {
      imageData.deviceInfo = {
        platform: deviceInfo.value.platform,
        model: deviceInfo.value.model,
        userAgent: deviceInfo.value.userAgent
      };
    }
    
    // Stocker l'image temporairement dans le stockage local
    await pendingImagesStore.setItem(imageData.id, imageData);
    
    // Utiliser également le service de synchronisation pour une gestion plus robuste
    try {
      // Importer dynamiquement le service de synchronisation
      const { syncService } = await import('@/services/syncService');
      
      // Stocker l'image avec le service pour une synchronisation ultérieure
      await syncService.storePendingImage(compressedFile, {
        id: imageData.id,
        deliveryId: props.deliveryId,
        deliveryContext: imageData.deliveryContext,
        type: 'delivery', // Identifier explicitement comme une image standard
        fileName: imageData.fileName,
        fileType: imageData.fileType,
        size: imageData.size
      });
      
      console.log('Image standard stockée pour synchronisation ultérieure:', imageData.id);
    } catch (storageError) {
      console.error('Erreur lors du stockage de l\'image pour synchronisation:', storageError);
      // Continuer malgré l'erreur - l'image est déjà dans pendingImagesStore
    }
    
    // Stocker l'image dans l'état local
    image.value = imageData;
    
    // Émettre l'événement de succès de prise de photo
    emit('success', imageData.id, imageData);
    
    // Si on est en ligne, commencer l'upload immédiatement
    if (isOnline.value) {
      try {
        await uploadImageToServer(imageData);
      } catch (uploadError) {
        console.error('Erreur initiale d\'upload:', uploadError);
        // L'erreur est déjà gérée dans uploadImageToServer
      }
    }
    
    return imageData;
  } catch (err) {
    console.error('Erreur lors du traitement de l\'image:', err);
    error.value = err.message || 'Échec du traitement de l\'image';
    emit('error', error.value);
    return null;
  } finally {
    loading.value = false;
    if (fileInput.value) {
      fileInput.value.value = ''; // Réinitialiser l'input pour permettre de prendre la même photo
    }
  }
};

// Gérer la sélection de fichiers
const handleFiles = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  
  if (!file.type.startsWith('image/')) {
    error.value = 'Seules les images sont autorisées'
    return
  }
  
  await processFile(file)
}

// Supprimer l'image
const removeImage = () => {
  // Si un upload est en cours, l'annuler
  if (uploading.value && abortController.value) {
    abortController.value.abort()
  }
  
  // Libérer l'URL de l'image
  if (localImageUrl.value) {
    URL.revokeObjectURL(localImageUrl.value)
    localImageUrl.value = null
  }
  
  // Libérer l'URL de la miniature
  if (image.value?.thumbnailUrl) {
    URL.revokeObjectURL(image.value.thumbnailUrl)
  }
  
  // Libérer l'URL de l'image originale
  if (image.value?.path && image.value.path.startsWith('blob:')) {
    URL.revokeObjectURL(image.value.path)
  }
  
  // Supprimer l'image du stockage local si nécessaire
  if (image.value?.id) {
    pendingImagesStore.removeItem(image.value.id)
  }
  
  // Réinitialiser l'état
  image.value = null
  uploadedServerId.value = null
  uploadProgress.value = 0
  error.value = null
}

// Annuler une opération
const cancelOperation = () => {
  if (cancelTimer.value) {
    clearTimeout(cancelTimer.value)
    cancelTimer.value = null
  }
  
  if (uploading.value && abortController.value) {
    abortController.value.abort()
  }
  
  loading.value = false
  uploading.value = false
  showCancelButton.value = false
  error.value = 'Opération annulée par l\'utilisateur'
}

// Méthode pour upload directement l'image au serveur
const uploadImageToServer = async (imageData) => {
  if (!isOnline.value) {
    // Si hors ligne, juste stocker localement
    return null
  }
  
  // Si l'image a déjà été synchronisée, ne rien faire
  if (imageData.synced) {
    uploadedServerId.value = imageData.serverImageId
    return imageData.serverImageId
  }
  
  // Vérifier si on a déjà essayé trop de fois
  const MAX_RETRY = 3
  if (imageData.retryCount >= MAX_RETRY) {
    console.error(`Abandon de l'upload après ${MAX_RETRY} tentatives échouées`)
    error.value = `L'upload a échoué après plusieurs tentatives. Veuillez réessayer ultérieurement.`
    emit('upload-error', new Error(error.value))
    return null
  }
  
  // Préparer l'upload
  uploading.value = true
  uploadProgress.value = 0
  
  try {
    // Créer un controller pour pouvoir annuler la requête
    abortController.value = new AbortController()
    
    // Créer le FormData pour l'upload
    const formData = new FormData();
    formData.append('image', imageData.file);
    formData.append('delivery_id', props.deliveryId);
    formData.append('deliveryContext', props.deliveryContext);
    
    // Log de ce qui est envoyé pour debug
    console.log('Envoi de FormData avec les clés:', [...formData.keys()]);
    console.log('delivery_id:', props.deliveryId, 'deliveryContext:', props.deliveryContext);
    
    // Ajouter des métadonnées supplémentaires
    if (deviceInfo.value) {
      formData.append('device_info', JSON.stringify(deviceInfo.value))
    }
    
    // Configuration de la requête avec tracking de progression
    const config = {
      signal: abortController.value.signal,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        uploadProgress.value = percentCompleted
        emit('upload-progress', percentCompleted)
      },
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // Augmenter le timeout pour les connexions lentes
      timeout: 60000 // 60 secondes
    }
    
    // Envoyer l'image au serveur
    const response = await axios.post('/livraison/deliveryImage/', formData, config)
    
    // Récupérer l'ID côté serveur
    const serverId = response.data.id
    uploadedServerId.value = serverId
    
    // Marquer l'image comme synchronisée
    imageData.synced = true
    imageData.serverImageId = serverId
    imageData.syncedAt = new Date().toISOString()
    
    // Mettre à jour le compteur de tentatives
    imageData.retryCount += 1
    
    // Stocker la mise à jour dans le pendingImagesStore
    await pendingImagesStore.setItem(imageData.id, imageData)
    
    // Émettre l'événement de succès avec l'ID serveur
    emit('upload-complete', serverId, imageData)
    
    return serverId
  } catch (err) {
    console.error('Erreur lors de l\'upload de l\'image:', err)
    
    // Si c'est une erreur d'annulation, ne pas la considérer comme une erreur
    if (err.name === 'AbortError') {
      console.log('Upload annulé par l\'utilisateur')
      return null
    }
    
    // Déterminer le type d'erreur
    let errorMessage = 'Erreur lors de l\'upload de l\'image'
    
    if (err.code === 'ECONNABORTED') {
      errorMessage = 'La connexion a expiré. Votre réseau est peut-être lent.'
    } else if (err.response) {
      // Erreur de réponse du serveur
      if (err.response.status >= 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer ultérieurement.'
      } else if (err.response.status === 413) {
        errorMessage = 'Image trop volumineuse pour le serveur.'
      } else if (err.response.status === 401 || err.response.status === 403) {
        errorMessage = 'Accès non autorisé. Veuillez vous reconnecter.'
      } else {
        errorMessage = `Erreur ${err.response.status}: ${err.response.data?.message || 'Erreur inconnue'}`
      }
    } else if (err.request) {
      // Erreur de réseau
      errorMessage = 'Impossible de joindre le serveur. Vérifiez votre connexion.'
    }
    
    error.value = errorMessage
    
    // Mettre à jour le compteur de tentatives
    imageData.retryCount = (imageData.retryCount || 0) + 1
    imageData.lastError = {
      message: errorMessage,
      timestamp: new Date().toISOString()
    }
    
    // Stocker la mise à jour dans le pendingImagesStore
    await pendingImagesStore.setItem(imageData.id, imageData)
    
    emit('upload-error', err)
    
    return null
  } finally {
    uploading.value = false
    abortController.value = null
  }
}

// Réessayer un upload qui a échoué
const retryUpload = async () => {
  if (!image.value || !image.value.id) return
  
  if (uploading.value) {
    console.log('Upload déjà en cours, impossible de réessayer maintenant')
    return
  }
  
  if (!isOnline.value) {
    error.value = 'Impossible de réessayer l\'upload hors ligne'
    return
  }
  
  error.value = null
  
  try {
    // Récupérer les données d'image à jour depuis le stockage
    const storedImage = await pendingImagesStore.getItem(image.value.id)
    
    if (storedImage) {
      // Si l'image est déjà synchronisée, ne rien faire
      if (storedImage.synced) {
        console.log('Image déjà synchronisée, aucune action nécessaire')
        uploadedServerId.value = storedImage.serverImageId
        return storedImage.serverImageId
      }
      
      // Mettre à jour l'image locale avec les données stockées
      image.value = storedImage
      
      // Réessayer l'upload
      return await uploadImageToServer(storedImage)
    } else {
      console.error('Impossible de trouver l\'image dans le stockage local')
      error.value = 'Image introuvable, impossible de réessayer'
    }
  } catch (err) {
    console.error('Erreur lors de la tentative de réessai:', err)
    error.value = 'Échec de la tentative de réessai'
  }
}

// Exposer les propriétés et méthodes nécessaires pour le composant parent
defineExpose({
  image,
  uploadedServerId,
  isUploading: uploading,
  progress: uploadProgress,
  takePicture,
  removeImage,
  cancelOperation,
  retryUpload
})
</script>

<template>
  <div class="progressive-uploader-wrapper">
    <div 
      class="uploader-container" 
      :class="{ 
        'has-image': image, 
        'is-uploading': uploading, 
        'compact-view': true,
        'upload-complete': image && uploadedServerId,
        'is-ios': isIOS,
        'is-android': isAndroid
      }" 
      @click="takePicture"
    >
      <!-- Input file pour la capture de photos - capture est toujours activé quel que soit l'appareil -->
      <input 
        type="file" 
        ref="fileInput" 
        accept="image/*" 
        capture="environment"
        @change="handleFiles"
        style="display: none" 
      />

      <!-- État vide - Pas d'image -->
      <div v-if="!image && !loading" class="empty-state">
        <div class="camera-section">
          <i class="pi pi-camera camera-icon"></i>
        </div>
        <div class="text-section">
          <span class="upload-text">
            Photo
          </span>
          <span class="upload-subtext">
            {{ isOnline ? 'Cliquer ici' : 'Hors ligne' }}
          </span>
        </div>
      </div>

      <!-- État de chargement - Traitement de l'image -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">Traitement...</div>
      </div>

      <!-- Aperçu de l'image avec barre de progression -->
      <div v-if="image && !loading" class="image-preview">
        <img 
          :src="image.thumbnailUrl || getImageUrl(image.path)" 
          class="preview-image" 
          :alt="`Image ${image.id}`" 
        />
        
        <!-- Bouton de suppression -->
        <button 
          type="button" 
          @click.stop="removeImage" 
          class="remove-button"
          aria-label="Supprimer l'image"
        >
          <i class="pi pi-times"></i>
        </button>
        
        <!-- Barre de progression pour l'upload -->
        <div v-if="uploading" class="upload-progress-container">
          <div class="upload-progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
          <span class="upload-progress-text">{{ uploadProgress }}%</span>
        </div>
        
        <!-- Indicateur d'état selon si l'image est uploadée ou en attente -->
        <div 
          v-if="!uploading" 
          class="status-indicator" 
          :class="{ 'success': uploadedServerId, 'pending': !uploadedServerId }"
        >
          <i :class="uploadedServerId ? 'pi pi-check' : 'pi pi-clock'"></i>
          <span class="indicator-text">
            {{ uploadedServerId ? 'Synchronisée' : (isOnline ? 'En attente' : 'Hors ligne') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Bouton d'annulation d'urgence -->
    <div v-if="showCancelButton && (loading || uploading)" class="emergency-button-container">
      <button 
        type="button"
        @click.stop="cancelOperation" 
        class="emergency-button"
        aria-label="Annuler l'opération en cours"
      >
        <i class="pi pi-times-circle"></i>
        Annuler
      </button>
    </div>

    <!-- Message d'erreur -->
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<style scoped>
/* Styles de base */
.progressive-uploader-wrapper {
  width: 100%;
  margin: 0 auto;
}

.uploader-container {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  width: 100%;
  height: 80px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0,0,0,0); /* Supprimer le highlight sur tap pour iOS */
}

.uploader-container.has-image {
  height: 120px;
}

.uploader-container.is-uploading {
  border-color: #3498db;
}

.uploader-container.upload-complete {
  border-color: #2ecc71;
}

/* Ajustements spécifiques iOS */
.uploader-container.is-ios {
  /* Safari sur iOS a besoin de tweaks spécifiques pour certains éléments */
  transform: translateZ(0); /* Force le GPU rendering pour améliorer les performances */
}

/* Style compact pour les vues étroites */
.uploader-container.compact-view {
  min-height: 60px;
  height: auto;
}

.uploader-container.compact-view.has-image {
  min-height: 90px;
  height: auto;
}

/* État vide */
.empty-state {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0.5rem;
  min-height: 60px;
}

.camera-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  width: 45px;
  height: 45px;
  background-color: #e9ecef;
  border-radius: 8px;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.compact-view .camera-section {
  min-width: 32px;
  width: 32px;
  height: 32px;
}

.camera-icon {
  font-size: 1.5rem;
  color: #6c757d;
}

.compact-view .camera-icon {
  font-size: 1rem;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
}

.upload-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compact-view .upload-text {
  font-size: 0.8rem;
}

.upload-subtext {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compact-view .upload-subtext {
  font-size: 0.7rem;
}

/* État de chargement */
.loading-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 100%;
  padding: 0 1rem;
  min-height: 60px;
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

.compact-view .loading-spinner {
  width: 1.5rem;
  height: 1.5rem;
  border-width: 2px;
}

.loading-text {
  font-size: 0.875rem;
  color: #3498db;
}

.compact-view .loading-text {
  font-size: 0.75rem;
}

/* Aperçu de l'image */
.image-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
  min-height: 90px;
}

.preview-image {
  height: 100%;
  width: 100%;
  max-width: 100%;
  object-fit: contain; /* Modifié de 'cover' à 'contain' pour éviter la déformation */
  border-radius: 6px;
  -webkit-user-drag: none; /* Empêcher le drag sur iOS */
  user-select: none; /* Empêcher la sélection */
  max-height: 100%; /* S'assurer que l'image ne dépasse pas son conteneur */
}

/* Bouton de suppression */
.remove-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  z-index: 2;
  -webkit-tap-highlight-color: transparent; /* Supprimer highlight sur tap mobile */
  /* Améliorer la taille de la zone tactile */
  padding: 0;
  touch-action: manipulation;
}

.compact-view .remove-button {
  width: 1.5rem;
  height: 1.5rem;
  top: 0.25rem;
  right: 0.25rem;
}

.compact-view .remove-button i {
  font-size: 0.8rem;
}

/* Créer une hit zone plus grande pour les appareils tactiles */
.is-ios .remove-button::before,
.is-android .remove-button::before {
  content: '';
  position: absolute;
  top: -10px;
  right: -10px;
  bottom: -10px;
  left: -10px;
  border-radius: 50%;
}

.remove-button:hover {
  background-color: rgba(231, 76, 60, 0.8);
}

/* Barre de progression */
.upload-progress-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
  padding: 0;
  display: flex;
  align-items: center;
}

.upload-progress-bar {
  height: 100%;
  background-color: #3498db;
  transition: width 0.2s ease;
}

.upload-progress-text {
  position: absolute;
  width: 100%;
  text-align: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Indicateur de statut */
.status-indicator {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
}

.status-indicator.success {
  background-color: rgba(46, 204, 113, 0.8);
}

.status-indicator.pending {
  background-color: rgba(243, 156, 18, 0.8);
}

.compact-view .status-indicator {
  bottom: 0.25rem;
  left: 0.25rem;
  padding: 0.15rem 0.35rem;
  font-size: 0.65rem;
}

.indicator-text {
  display: inline;
}

.compact-view .indicator-text {
  display: none;
}

/* Bouton d'annulation d'urgence */
.emergency-button-container {
  margin-top: 0.5rem;
  text-align: center;
}

.emergency-button {
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
  transition: background-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.emergency-button:hover {
  background-color: #c82333;
}

.emergency-button i {
  font-size: 1.25rem;
}

/* Message d'erreur */
.error-message {
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.5rem;
}

/* Animation de rotation */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Adaptations pour les appareils mobiles */
@media (max-width: 768px) {
  .uploader-container:not(.compact-view) {
    height: 100px;
    border-width: 3px;
  }
  
  .uploader-container:not(.compact-view).has-image {
    height: 160px;
  }
  
  .camera-section:not(.compact-view) {
    width: 60px;
    height: 60px;
  }
  
  .camera-icon:not(.compact-view) {
    font-size: 2rem;
  }
  
  .upload-text:not(.compact-view) {
    font-size: 1rem;
  }
  
  .remove-button:not(.compact-view) {
    width: 2.5rem;
    height: 2.5rem;
    top: 0.75rem;
    right: 0.75rem;
  }
  
  .remove-button:not(.compact-view) i {
    font-size: 1.25rem;
  }
  
  .emergency-button {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
}

/* Styles spécifiques pour iOS Safari */
@supports (-webkit-touch-callout: none) {
  .uploader-container {
    /* Correctifs spécifiques Safari iOS */
    will-change: transform; /* Améliore les performances de rendu */
  }
  
  .preview-image {
    /* Éviter les problèmes de ratio sur iOS */
    object-position: center;
  }
}

/* Styles pour le mode tactile */
@media (hover: none) {
  .uploader-container:active {
    background-color: #e2e6ea;
  }
  
  .remove-button:active {
    background-color: rgba(231, 76, 60, 0.8);
  }
  
  .emergency-button:active {
    background-color: #c82333;
  }
  
  /* Augmenter les zones tactiles sur mobile */
  .remove-button {
    min-width: 36px;
    min-height: 36px;
  }
}
</style> 